import { PLATFORM } from "core-lib/data/common";
import {
  updateLastLocationResponse,
  updateLocationRequest,
  updateLocationResponse,
} from "core-lib/reduxStore/layout/action";
import { Address, AddressStatusType } from "core-lib/types/Address";
import {
  LastLocationResponse,
  LocationRequestResponse,
  UserGeoAddressType,
  UserLocation,
  UserLocationData,
  UserLocationRequestType,
  UserLocationRequirementType,
} from "core-lib/types/Location";
import { NativePermission } from "core-lib/types/NativePermission";
import { getPlatform } from "core-lib/utils/helpers";
import {
  checkGPS,
  checkLocationPermission,
  getLocation,
  getReverseGeoCodeData,
  openGpsSettings,
  openLocationSettings,
  requestGPS,
  requestLocationPermission,
} from "core-lib/utils/locationV2";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AnimatedImage, BackButton, Button, Modal, Typography } from "ui";
import { TypeScales } from "ui/primitives";
import s from "./LocationHandler.module.scss";
import {
  getValueFromNativeSession,
  isNativeSessionStorageAvailable,
  setValueInNativeSession,
} from "core-lib/utils/nativeSession";
import { NativeSessionStorage } from "core-lib/constants/storage";

export const LocationHandler = () => {
  const dispatch = useDispatch();

  const locationRequest: UserLocation = useSelector(
    (state: any) => state.layout?.userLocationRequest
  );

  const userLocData: UserLocationData = useSelector(
    (state: any) => state.layout?.userLocationResponse?.data
  );

  const [askLocation, setAskLocation] = useState<
    "GPS" | "LOCATION" | "REQUEST_GPS" | "REQUEST_LOCATION" | "NONE"
  >("NONE");

  const requestGpsPermissionHandler = async (): Promise<boolean> => {
    const { response, error } = await requestGPS();

    if (response.data) {
      return !!response?.data;
    } else {
      if (error) toast.error(error);
      return false;
    }
  };

  const updateUserLocationResponse = (userLocation) => {
    dispatch(
      updateLocationResponse({
        data: { ...userLocation },
      })
    );
  };

  const saveLastLocationResponse = (userLocation) => {
    dispatch(
      updateLastLocationResponse({
        data: { ...userLocation },
      })
    );
  };

  const saveLocationResponse = (userLocation) => {
    updateUserLocationResponse(userLocation);
    dispatch(
      updateLocationRequest({
        ...locationRequest,
        payload:
          userLocation.status === AddressStatusType.Success
            ? null
            : locationRequest.payload,
        requestType: UserLocationRequestType.Default,
      })
    );
  };

  const openGpsBottomSheetIfRequired = async (requestGpsFromUser) => {
    if (
      ![UserLocationRequirementType.LocationShowNoNudgeToUser].includes(
        locationRequest.payload.locationRequirementType
      )
    ) {
      if (isNativeSessionStorageAvailable()) {
        // show bottomsheet if required
        const { response: isBottomSheetAlreadyShownInSession } =
          await getValueFromNativeSession(
            NativeSessionStorage.ShownLocationDenied
          );
        if (
          !!isBottomSheetAlreadyShownInSession &&
          ![UserLocationRequirementType.LocationMandatory].includes(
            locationRequest.payload.locationRequirementType
          )
        ) {
          return;
        }

        if (!requestGpsFromUser) {
          // set the value in session
          setValueInNativeSession(
            NativeSessionStorage.ShownLocationDenied,
            "true"
          );
          setAskLocation("REQUEST_GPS");
        } else {
          setAskLocation("GPS");
        }
      } else {
        if (!requestGpsFromUser) {
          setAskLocation("REQUEST_GPS");
        } else {
          setAskLocation("GPS");
        }
      }
    }
  };

  const openBottomSheetIfRequired = async (
    requestLocFromUser,
    locationPermission: NativePermission
  ) => {
    if (
      ![UserLocationRequirementType.LocationShowNoNudgeToUser].includes(
        locationRequest.payload.locationRequirementType
      )
    ) {
      if (requestLocFromUser) {
        setAskLocation("LOCATION");
      } else {
        if (isNativeSessionStorageAvailable()) {
          const { response: isBottomSheetAlreadyShownInSession } =
            await getValueFromNativeSession(
              NativeSessionStorage.ShownLocationDenied
            );
          if (
            !!isBottomSheetAlreadyShownInSession &&
            ![UserLocationRequirementType.LocationMandatory].includes(
              locationRequest.payload.locationRequirementType
            )
          ) {
            return;
          }

          // set the value in session
          setValueInNativeSession(
            NativeSessionStorage.ShownLocationDenied,
            "true"
          );
          if (locationPermission === NativePermission.Denied) {
            setAskLocation("REQUEST_LOCATION");
          } else {
            setAskLocation("LOCATION");
          }
        } else {
          if (locationPermission === NativePermission.Denied) {
            setAskLocation("REQUEST_LOCATION");
          } else {
            setAskLocation("LOCATION");
          }
        }
      }
    }
  };

  const locationGeoAddress = async (
    latLng: {
      lat: number;
      lon: number;
    } = null
  ): Promise<{
    geoAddress: Address;
  }> => {
    const geoAddressTypeVal =
      locationRequest.requestType === UserLocationRequestType.RequestGeoAddress
        ? locationRequest.payload.geoAddressType
        : UserGeoAddressType.LatLng;

    // fetch the geo address based on the configuration
    const { response: geoCodeResponse, error: geoCodeError } =
      await getReverseGeoCodeData({
        locationType: geoAddressTypeVal,
        latLng: latLng,
      });

    if (geoCodeResponse) {
      return {
        geoAddress: geoCodeResponse,
      };
    } else {
      return {
        geoAddress: null,
      };
    }
  };

  const locationLatLng = async (): Promise<{
    latLng: LocationRequestResponse;
  }> => {
    if (!!locationRequest.payload.givenLatLng) {
      return {
        latLng: locationRequest.payload.givenLatLng,
      };
    } else if (
      locationRequest.requestType !== UserLocationRequestType.RequestGeoAddress
    ) {
      // fetch lat lng

      const { response: coordinates, error: coordinatesErr } =
        await getLocation(locationRequest.payload.locationType);

      if (coordinates) {
        return {
          latLng: coordinates,
        };
      } else {
        toast.error(coordinatesErr || "Unable to fetch your location");
        return {
          latLng: null,
        };
      }
    }
    return {
      latLng: null,
    };
  };

  const lastKnownLocationHandler = async () => {
    let lastLocationData: LastLocationResponse = {
      status: AddressStatusType.Pending,
      data: null,
    };

    saveLastLocationResponse(lastLocationData);

    const geoAddressTypeVal = UserGeoAddressType.Last;

    // fetch the geo address based on the configuration
    const { response: geoCodeResponse, error: geoCodeError } =
      await getReverseGeoCodeData({
        locationType: geoAddressTypeVal,
      });

    if (geoCodeResponse) {
      lastLocationData = {
        ...lastLocationData,
        data: geoCodeResponse,
        status: AddressStatusType.Success,
      };
    } else {
      lastLocationData = {
        ...lastLocationData,
        status: AddressStatusType.Failed,
      };
    }
    saveLastLocationResponse({ lastLocationData });
  };

  const locationMaster = async (requestGpsFromUser = false) => {
    let userLoc: UserLocationData = {
      status: AddressStatusType.Pending,
      coords: null,
      data: null,
    };

    // basic Configs
    let isGpsEnabled = false;
    let isLocationPermissionEnabled = false;
    updateUserLocationResponse(userLoc);

    // // ask for last known location handler
    // lastKnownLocationHandler();

    const { response: gpsPermssion, error } = await checkGPS();
    if (!gpsPermssion?.data) {
      const platform = getPlatform();
      if (platform === PLATFORM.android && requestGpsFromUser)
        isGpsEnabled = await requestGpsPermissionHandler();
    } else {
      isGpsEnabled = !!gpsPermssion?.data;
    }
    if (!isGpsEnabled) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
      saveLocationResponse(userLoc);
      openGpsBottomSheetIfRequired(requestGpsFromUser);

      return;
    }
    ////////////////////////////////
    // GPS is enabled now

    let { response: locationPermission, error: locationPermissionError } =
      await checkLocationPermission();
    if (!locationPermission || locationPermissionError) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
      saveLocationResponse(userLoc);
      return;
    }
    if (requestGpsFromUser && locationPermission === NativePermission.Denied) {
      const {
        response: locationPermissionRes,
        error: locationPermissionError,
      } = await requestLocationPermission();
      if (locationPermissionRes) {
        locationPermission = locationPermissionRes;
      }
    }
    if (locationPermission !== NativePermission.Accepted) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
      openBottomSheetIfRequired(requestGpsFromUser, locationPermission);
      saveLocationResponse(userLoc);
      return;
    }

    ////////////////////////////////
    // LOCATION PERMISSION IS GRANTED TO APP NOW
    isLocationPermissionEnabled = true;

    const { latLng } = await locationLatLng();

    if (
      !latLng &&
      locationRequest.requestType !== UserLocationRequestType.RequestGeoAddress
    ) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
      saveLocationResponse(userLoc);
      return;
    }

    // not able to get lat lng for some reason(edge case)
    if (!latLng) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
      saveLocationResponse(userLoc);
      return;
    } else {
      userLoc = {
        ...userLoc,
        coords: latLng,
        data: {
          latitude: latLng.lat,
          longitude: latLng.lon,
          locality: "",
        },
      };
    }

    /// only lat lng was required, from parent, all work is done
    if (locationRequest.requestType === UserLocationRequestType.RequestLatLng) {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Success,
      };
      saveLocationResponse(userLoc);
      return;
    }

    const { geoAddress } = await locationGeoAddress(latLng);

    if (geoAddress) {
      userLoc = {
        ...userLoc,
        data: geoAddress,
        status: AddressStatusType.Success,
      };
    } else {
      userLoc = {
        ...userLoc,
        status: AddressStatusType.Failed,
      };
    }
    saveLocationResponse(userLoc);
  };

  const onManualCloseBtmSheet = () => {
    updateUserLocationResponse({
      ...userLocData,
      status: AddressStatusType.Denied,
    });
    setAskLocation("NONE");
  };

  useEffect(() => {
    if (
      locationRequest &&
      locationRequest.requestType !== UserLocationRequestType.Default
    ) {
      locationMaster();
    }
  }, [locationRequest]);

  return (
    <div className={s.locationModals}>
      <Modal
        show={askLocation !== "NONE"}
        head={{
          show: false,
          title: "",
          onClose: () => {
            onManualCloseBtmSheet();
          },
        }}
        customeBodyStyle={{ padding: "0px" }}
        rootStyle={{
          zIndex: "1000",
        }}
        closeOnClickOutside={false}
      >
        <div className={s.confirm_screen}>
          <div className={s.hide}>
            <BackButton
              onBack={() => {
                onManualCloseBtmSheet();
              }}
            />
          </div>
          <div className={s.wrapper}>
            <div className={s.img}>
              <AnimatedImage src="https://parkplus-bkt-img.parkplus.io/production/pwa/public/address_20240422154522891525.png" />
            </div>

            <div className={s.content_text}>
              <Typography
                label={
                  locationRequest?.bottomsheetData?.title ||
                  "Allow location access"
                }
                variant={TypeScales.h3}
              />
            </div>
            <div className={s.content_subtext}>
              <Typography
                label={
                  locationRequest?.bottomsheetData?.subtitle ||
                  "Turn on exact location access for a better experience while using the app"
                }
                variant={TypeScales.base_big_r}
                color="text_subheading"
              />
            </div>
          </div>
          <div className={s.cta}>
            <Button
              label="Share my location"
              style={{ width: "100%" }}
              onClick={async () => {
                if (askLocation === "GPS") {
                  setAskLocation("NONE");
                  const { response, error } = await openGpsSettings();
                  if (response?.data) {
                    locationMaster();
                  } else {
                    setAskLocation("GPS");
                  }
                } else if (askLocation === "LOCATION") {
                  setAskLocation("NONE");
                  const { response, error } = await openLocationSettings();
                  if (
                    [
                      NativePermission.Accepted,
                      NativePermission.Denied,
                    ].includes(response)
                  ) {
                    locationMaster();
                  } else setAskLocation("LOCATION");
                } else if (askLocation === "REQUEST_GPS") {
                  setAskLocation("NONE");
                  setTimeout(() => {
                    locationMaster(true);
                  }, 100);
                } else if (askLocation === "REQUEST_LOCATION") {
                  setAskLocation("NONE");
                  setTimeout(() => {
                    locationMaster(true);
                  }, 100);
                }
              }}
            />

            <div className={s.dismissCta}>
              <Button
                label="Dismiss"
                onClick={() => {
                  onManualCloseBtmSheet();
                }}
                style={{ width: "100%" }}
                variant="PrimaryNaked"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
